// The app: sign-in, then the conversation. History comes from GET /api/conversation; new items,
// live text and turn states from GET /api/stream, reopened with the newest seq whenever it closes.
// The look is AlphaLab's (design-revamp.md section 4); the chat is drawn by assistant-ui (section 5).
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { get, post } from "./api.js";
import { DriveUploads, browserTransport, type AttachmentLimits, type AttachmentRef } from "./attachments.js";
import { composerBusy, emptyChat, endNotice, isRunning, lastSeq, reduce, workingNotice, type Item, type Turn } from "./chat.js";
import { AgendaPanel } from "./components/Agenda.js";
import { TasksPanel } from "./components/Tasks.js";
import { statusLine, type TasksView } from "./tasks.js";
import { Confirm, type ConfirmRequest } from "./components/Confirm.js";
import { ByAlphaLab, Header, QuantyLogo } from "./components/Header.js";
import { Panel, type PanelState } from "./components/Panel.js";
import { OpenLinkContext, Thread } from "./components/Thread.js";
import { Button } from "./components/ui/button.js";
import { isAlphaLabUrl, isWide, shownUrl } from "./links.js";
import { alphalabOrigin } from "./theme.js";
import { thinking, threadOf } from "./thread.js";

interface Me {
  user: { id: string; email?: string };
  connection: "connected" | "reconnect_required" | "unavailable";
  /** Scopes Quanty One now asks for that this grant lacks: reconnecting grants them. */
  missingScopes?: string[];
  /** Present where the environment asks AlphaLab for both Drive scopes (attachments.md A7). */
  attachments?: AttachmentLimits;
}

const DRIVE_SCOPES = ["drive:read", "drive:write"];

const SIGN_IN_REASONS: Record<string, string> = {
  expired: "The sign-in took too long. Sign in again.",
  denied: "AlphaLab did not allow the connection.",
  unavailable: "AlphaLab could not be reached. Try again in a moment.",
  failed: "The sign-in did not complete.",
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function App() {
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [unreachable, setUnreachable] = useState(false);
  const signInReason = new URLSearchParams(location.search).get("signin");

  // Only a 401 means signed out; any other failure keeps what is shown, or offers a retry before
  // anything was shown.
  const loadMe = useCallback(async () => {
    try {
      const res = await get<Me>("/api/me");
      if (res.status === 200) {
        setMe(res.data);
        setUnreachable(false);
        return;
      }
      if (res.status === 401) return setMe(null);
    } catch {
      // Fall through.
    }
    setUnreachable(true);
  }, []);
  const onSignedOut = useCallback(() => setMe(null), []);

  useEffect(() => {
    void loadMe();
  }, [loadMe]);

  if (me === undefined && unreachable) {
    return (
      <Centered>
        <p className="text-sm text-muted-foreground">Quanty One could not be reached.</p>
        <Button onClick={() => void loadMe()}>Try again</Button>
      </Centered>
    );
  }
  if (me === undefined) return <Centered><p className="text-sm text-muted-foreground">Loading…</p></Centered>;
  if (me === null || signInReason) return <SignIn reason={signInReason ?? undefined} />;
  // A different account (signed in from another tab) gets a conversation of its own.
  return <Conversation key={me.user.id} me={me} refreshMe={loadMe} onSignedOut={onSignedOut} />;
}

function Centered({ children }: { children: React.ReactNode }) {
  return <main className="flex min-h-full flex-col items-center justify-center gap-4 px-4 text-center">{children}</main>;
}

function SignIn({ reason }: { reason?: string }) {
  return (
    <Centered>
      <h1 className="flex flex-col items-center gap-2">
        <QuantyLogo className="h-9" />
        <ByAlphaLab />
      </h1>
      <p className="max-w-sm text-sm text-muted-foreground">Your AI quant researcher.</p>
      {reason && <p className="rounded-md bg-error-bg px-3 py-2 text-sm text-error">{SIGN_IN_REASONS[reason] ?? SIGN_IN_REASONS.failed}</p>}
      <Button asChild>
        <a href="/api/auth/login">{reason ? "Try again" : "Sign in with AlphaLab"}</a>
      </Button>
    </Centered>
  );
}

function Conversation({ me, refreshMe, onSignedOut }: { me: Me; refreshMe: () => Promise<void>; onSignedOut: () => void }) {
  const [chat, dispatch] = useReducer(reduce, emptyChat);
  const [notice, setNotice] = useState<string>();
  const [showAgenda, setShowAgenda] = useState(false);
  const [showTasks, setShowTasks] = useState(false);
  const [tasks, setTasks] = useState<TasksView>();
  const [panel, setPanel] = useState<PanelState>();
  const [wide, setWide] = useState(isWide());
  const [confirm, setConfirm] = useState<ConfirmRequest>();
  const lastAuthCheck = useRef(0);
  // False once this conversation is gone (signed out, or another account): sends stop retrying.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const chatRef = useRef(chat);
  chatRef.current = chat;

  // Attachments, where offered: uploads need a grant with the Drive scopes, and until the person
  // reconnects the paperclip says so (attachments.md section 7.4).
  const maxFiles = me.attachments?.maxFiles;
  const maxBytes = me.attachments?.maxBytes;
  const uploads = useMemo(
    () => (maxFiles && maxBytes ? new DriveUploads({ maxFiles, maxBytes }, browserTransport(me.user.id, () => void refreshMe())) : undefined),
    [maxFiles, maxBytes, me.user.id, refreshMe],
  );
  const driveGranted = me.connection !== "reconnect_required" && !me.missingScopes?.some((s) => DRIVE_SCOPES.includes(s));

  // The tasks' status line: read every minute, and again when a turn ends (tasks.md section 13).
  const turnState = chat.turn?.state;
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await get<TasksView>("/api/tasks");
        if (!cancelled && res.status === 200) setTasks(res.data);
      } catch {
        // The status line is an extra; the conversation goes on without it.
      }
    };
    void load();
    const timer = setInterval(() => void load(), 60_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [turnState]);

  // The panel exists only on a wide screen; narrowing closes it (section 8).
  useEffect(() => {
    const onResize = () => {
      const w = isWide();
      setWide(w);
      if (!w) setPanel(undefined);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const openLink = useCallback((url: string, title?: string): boolean => {
    if (!isWide() || !isAlphaLabUrl(url)) return false;
    setPanel({ url, ...(title ? { title } : {}) });
    return true;
  }, []);

  /** Reads the shown turn back by ID while it still looks running here. */
  const recheckTurn = useCallback(async () => {
    const turn = chatRef.current.turn;
    if (!isRunning(turn)) return;
    try {
      const res = await get<Turn>(`/api/turns/${turn!.turnId}`);
      if (res.status === 200) dispatch({ type: "turn", turn: res.data });
    } catch {
      // The next reopen tries again.
    }
  }, []);

  const loadHistory = useCallback(async (before?: number): Promise<boolean> => {
    const res = await get<{ items: Item[]; before?: number }>(`/api/conversation${before ? `?before=${before}` : ""}`);
    if (res.status === 401) {
      onSignedOut();
      return true;
    }
    if (res.status !== 200) return false;
    dispatch({ type: "history", items: res.data.items, before: res.data.before, replace: before === undefined });
    return true;
  }, [onSignedOut]);

  // The stream: opened after the first history page, reopened with the newest seq when it ends.
  useEffect(() => {
    let source: EventSource | undefined;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const open = () => {
      if (stopped) return;
      source = new EventSource(`/api/stream?after=${lastSeq(chatRef.current)}`);
      const reopen = (delay: number) => {
        source?.close();
        clearTimeout(timer);
        timer = setTimeout(open, delay);
      };
      source.addEventListener("item", (e) => {
        const item = JSON.parse((e as MessageEvent).data) as Item;
        dispatch({ type: "item", item });
        // Quanty One showing something in AlphaLab opens the panel, only for a call made while this
        // page is open, never from history (section 8).
        if (item.tool === "quanty_show_in_alphalab" && item.status === "completed") {
          const shown = shownUrl(item.args);
          if (shown) openLink(shown.url, shown.title);
        }
      });
      source.addEventListener("delta", (e) => dispatch({ type: "delta", ...JSON.parse((e as MessageEvent).data) }));
      source.addEventListener("turn", (e) => dispatch({ type: "turn", turn: JSON.parse((e as MessageEvent).data) as Turn }));
      source.addEventListener("reset", () => dispatch({ type: "reset" }));
      // The conversation was reset in another tab or on another device.
      source.addEventListener("cleared", () => dispatch({ type: "cleared" }));
      source.addEventListener("signed_out", () => {
        stopped = true;
        source?.close();
        onSignedOut();
      });
      source.addEventListener("reconnect", () => reopen(0));
      // The stream names only an active turn: one that ended while it was down is read back by ID.
      source.onopen = () => void recheckTurn();
      // EventSource would retry with the old cursor; reopen with the newest one instead. A stream
      // that keeps failing may mean the session ended: /api/me is read again, at most every 30 s.
      source.onerror = () => {
        if (Date.now() - lastAuthCheck.current > 30_000) {
          lastAuthCheck.current = Date.now();
          void refreshMe();
        }
        reopen(1000);
      };
    };
    // The stream opens even when the first page fails: from seq 0 it replays, or sends `reset`,
    // which loads the history again.
    void loadHistory()
      .catch(() => undefined)
      .then(open);
    return () => {
      stopped = true;
      clearTimeout(timer);
      source?.close();
    };
  }, [loadHistory, onSignedOut, refreshMe, recheckTurn, openLink]);

  // A reload the stream asked for is tried until it works, waiting longer each time, up to 30 seconds.
  useEffect(() => {
    if (!chat.reload) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const attempt = async (wait: number) => {
      const loaded = await loadHistory().catch(() => false);
      if (!loaded && !cancelled) timer = setTimeout(() => void attempt(Math.min(wait * 2, 30_000)), wait);
    };
    void attempt(2_000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [chat.reload, loadHistory]);

  // How the last turn sent from this browser ended outlives a page reload: once it has ended the
  // stream no longer names it, so it is read back by its ID.
  useEffect(() => {
    const turnId = loadLastTurn(me.user.id);
    if (!turnId) return;
    void get<Turn>(`/api/turns/${turnId}`)
      .then((res) => {
        if (res.status === 200) dispatch({ type: "turn", turn: res.data });
      })
      .catch(() => undefined);
  }, [me.user.id]);

  /**
   * Sends a message (section 6): it shows at once (P1), then the request goes out with its key. A
   * lost answer or a 5xx other than 503 proves nothing, so the same key is sent again every 3
   * seconds for up to 2 minutes (P3); a message during scheduled work is repeated every 2 seconds
   * while the API answers `interrupting`.
   */
  const send = async (text: string, attachments: AttachmentRef[] = [], key: string = crypto.randomUUID()) => {
    const held = chatRef.current.pending;
    if (held && held.key !== key && (held.state === "sending" || held.state === "unconfirmed")) return;
    dispatch({ type: "pending", key, text, attachments, at: Date.now() });
    setNotice(undefined);
    const giveUpAt = Date.now() + 2 * 60_000;
    const interruptUntil = Date.now() + 10 * 60_000;
    for (;;) {
      if (!alive.current) return;
      let res: Awaited<ReturnType<typeof post<{ turnId?: string; state?: string; error?: string; interrupting?: boolean }>>> | undefined;
      try {
        // The API refuses it for any other signed-in account (user_changed).
        res = await post("/api/messages", { text, ...(attachments.length ? { attachments } : {}) }, { "idempotency-key": key, "x-quanty-user": me.user.id });
      } catch {
        res = undefined;
      }
      if (!alive.current) return;
      if (!res || (res.status >= 500 && res.status !== 503)) {
        if (Date.now() >= giveUpAt) {
          dispatch({ type: "pending_failed", key, state: "unconfirmed", reason: "No answer from Quanty One yet. Retry sends it again as the same message." });
          return;
        }
        await sleep(3_000);
        continue;
      }
      if (res.status === 409 && res.data.interrupting && Date.now() < interruptUntil) {
        setNotice("Interrupting scheduled work…");
        await sleep(2_000);
        continue;
      }
      setNotice(undefined);
      if (res.status === 202 && res.data.turnId) {
        saveLastTurn(me.user.id, res.data.turnId);
        dispatch({ type: "pending_accepted", key, turnId: res.data.turnId });
        dispatch({ type: "turn", turn: { turnId: res.data.turnId, state: (res.data.state ?? "recorded") as Turn["state"] } });
        return;
      }
      if (res.status === 401) {
        // An ended browser session signs out; a grant that needs renewing shows the Reconnect banner.
        if (res.data.error === "signed_out") return onSignedOut();
        void refreshMe();
      }
      // Another account is signed in now: this page reloads as that account's.
      if (res.status === 409 && res.data.error === "user_changed") return void refreshMe();
      const reasons: Record<number, string> = {
        401: "Your AlphaLab connection needs to be renewed. Use Reconnect above.",
        409: "Quanty One is still working on your last message. Wait for it, or press Stop.",
        503: "Quanty One is not taking messages right now. Try again shortly.",
      };
      const reason = res.data.error === "bad_attachments" ? "Quanty One could not take the files on it." : reasons[res.status];
      dispatch({ type: "pending_failed", key, state: "not_sent", reason: reason ?? "The message was not sent." });
      return;
    }
  };

  const stop = async () => {
    if (!isRunning(chatRef.current.turn)) return;
    try {
      const res = await post<Turn & { error?: string }>("/api/turns/current/cancel");
      if (res.status === 202) return dispatch({ type: "turn", turn: res.data });
      // Nothing running any more: read back how the shown turn ended.
      if (res.status === 409) return void recheckTurn();
      if (res.status === 401) {
        if (res.data.error === "signed_out") return onSignedOut();
        void refreshMe();
      }
    } catch {
      // Fall through to the notice.
    }
    setNotice("Stop did not go through. Press Stop again.");
  };

  const resetConversation = async () => {
    try {
      const res = await post("/api/conversation/reset");
      if (res.status === 200) {
        dispatch({ type: "cleared" });
        setNotice(undefined);
        return;
      }
      if (res.status === 401) return onSignedOut();
      if (res.status === 409) return setNotice("Quanty One is still working. Stop it first, then reset the conversation.");
    } catch {
      // Fall through to the notice.
    }
    setNotice("The conversation was not reset. Try again.");
  };

  const signOut = async (everywhere: boolean) => {
    try {
      // Signed out only once the server says so (or that the session is already gone).
      const res = await post(everywhere ? "/api/auth/logout-everywhere" : "/api/auth/logout");
      if (res.status === 200 || res.status === 401) return onSignedOut();
    } catch {
      // Fall through to the notice.
    }
    setNotice("Signing out did not complete. Try again.");
  };

  // "Today" and "Yesterday" move on at midnight, even when nothing new arrives.
  const [day, setDay] = useState(() => new Date().toDateString());
  useEffect(() => {
    const midnight = new Date();
    midnight.setHours(24, 0, 1, 0);
    const timer = setTimeout(() => setDay(new Date().toDateString()), midnight.getTime() - Date.now());
    return () => clearTimeout(timer);
  }, [day]);
  const messages = useMemo(() => threadOf(chat), [chat, day]);
  const running = isRunning(chat.turn);
  const ended = endNotice(chat.turn);
  const pending = chat.pending;
  const working = thinking(chat) ? (running ? workingNotice(chat.turn) : "Quanty One is thinking…") : undefined;
  // What the task driver is doing, shown when no turn runs (tasks.md section 13).
  const taskStatus = working ? undefined : statusLine(tasks);
  const appUrl = alphalabOrigin();

  const status = (
    <div className="space-y-2 pb-2">
      {notice && <p className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">{notice}</p>}
      {ended && !running && <p className="text-sm text-muted-foreground">{ended}</p>}
      {taskStatus && (
        <button type="button" className="text-left text-sm text-muted-foreground underline-offset-2 hover:underline" onClick={() => setShowTasks(true)}>
          {taskStatus}
        </button>
      )}
      {pending && (pending.state === "not_sent" || pending.state === "unconfirmed") && (
        <div className="flex flex-wrap items-center gap-2 rounded-md bg-warning-bg px-3 py-2 text-sm text-warning">
          <span className="flex-1">{pending.state === "not_sent" ? "Not sent." : "Not confirmed."} {pending.reason}</span>
          <Button size="sm" variant="outline" onClick={() => void send(pending.text, pending.attachments, pending.key)}>
            Retry
          </Button>
          {pending.state === "not_sent" && (
            <Button size="sm" variant="ghost" onClick={() => dispatch({ type: "pending_discard", key: pending.key })}>
              Discard
            </Button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <OpenLinkContext.Provider value={openLink}>
      <div className="flex h-full flex-col">
        <Header
          email={me.user.email}
          alphalabUrl={appUrl}
          scheduledOpen={showAgenda}
          onScheduled={() => setShowAgenda((v) => !v)}
          tasksOpen={showTasks}
          onTasks={() => setShowTasks((v) => !v)}
          onOpenAlphaLab={(url) => {
            if (!openLink(url, "AlphaLab")) window.open(url, "_blank", "noopener,noreferrer");
          }}
          onLogOut={() => void signOut(false)}
          onResetConversation={() =>
            setConfirm({
              title: "Reset Quanty One's conversation?",
              body:
                "Quanty One forgets this conversation and starts a new one.\n\n" +
                "This does not reset AlphaLab: your projects, AlphaGraphs, runs, notes and documents stay as they are. " +
                "Scheduled check-ins stay too; you can cancel them under Scheduled.",
              confirm: "Reset conversation",
              onConfirm: () => void resetConversation(),
            })
          }
          onSignOutEverywhere={() =>
            setConfirm({
              title: "Sign out everywhere?",
              body: "This signs you out on every device and stops Quanty One's current work.",
              confirm: "Sign out everywhere",
              onConfirm: () => void signOut(true),
            })
          }
        />
        {me.connection === "reconnect_required" && (
          <p className="border-b border-border bg-warning-bg px-4 py-2 text-sm text-warning">
            Quanty One can no longer reach your AlphaLab account.{" "}
            <a className="font-medium underline" href="/api/auth/login">
              Reconnect
            </a>
          </p>
        )}
        {me.connection === "connected" && (me.missingScopes?.length ?? 0) > 0 && (
          <p className="border-b border-border bg-warning-bg px-4 py-2 text-sm text-warning">
            Reconnect AlphaLab to give Quanty One its new permissions.{" "}
            <a className="font-medium underline" href="/api/auth/login">
              Reconnect
            </a>
          </p>
        )}
        <div className="flex min-h-0 flex-1">
          {/* With AlphaLab open, the panel takes most of the screen and the chat at most 800px. */}
          <main className={wide && panel ? "w-[800px] min-w-[420px] max-w-[45%] shrink-0" : "min-w-[420px] flex-1 max-sm:min-w-0"}>
            <Thread
              messages={messages}
              running={running}
              busy={composerBusy(chat)}
              loaded={chat.loaded}
              thinking={working}
              onLoadEarlier={chat.before ? () => void loadHistory(chat.before) : undefined}
              onSend={(text, attachments) => void send(text, attachments)}
              onStop={() => void stop()}
              status={status}
              uploads={driveGranted ? uploads : undefined}
              reconnectToAttach={!!uploads && !driveGranted}
            />
          </main>
          {wide && panel && <Panel state={panel} onClose={() => setPanel(undefined)} />}
          {showAgenda && (
            // A drawer over the chat at every width, so it never adds to the columns' minimum widths.
            <div
              className="fixed inset-0 top-12 z-40 flex justify-end bg-black/30"
              onClick={(e) => {
                if (e.target === e.currentTarget) setShowAgenda(false);
              }}
            >
              <AgendaPanel onSignedOut={onSignedOut} onClose={() => setShowAgenda(false)} confirm={setConfirm} />
            </div>
          )}
          {showTasks && (
            <div
              className="fixed inset-0 top-12 z-40 flex justify-end bg-black/30"
              onClick={(e) => {
                if (e.target === e.currentTarget) setShowTasks(false);
              }}
            >
              <TasksPanel
                alphalabUrl={appUrl}
                onSignedOut={onSignedOut}
                onClose={() => setShowTasks(false)}
                onOpenAlphaLab={(url) => {
                  if (!openLink(url, "AlphaLab")) window.open(url, "_blank", "noopener,noreferrer");
                }}
              />
            </div>
          )}
        </div>
        <Confirm request={confirm} onClose={() => setConfirm(undefined)} />
      </div>
    </OpenLinkContext.Provider>
  );
}

const LAST_TURN_KEY = "quanty.lastTurn.";

function loadLastTurn(userId: string): string | undefined {
  try {
    return localStorage.getItem(LAST_TURN_KEY + userId) ?? undefined;
  } catch {
    return undefined;
  }
}

function saveLastTurn(userId: string, turnId: string): void {
  try {
    localStorage.setItem(LAST_TURN_KEY + userId, turnId);
  } catch {
    // Kept for this page only.
  }
}
